import { QuotaType } from '@bpinternal/const'
import { useSuspenseQuery as useTanstackSuspenseQuery } from '@tanstack/react-query'
import { BreakdownCard, CumulativeAiSpend, ProgressBarCard } from '~/features/usage/components'
import { QUOTA_TYPE_USER_FACING_PROPERTIES_MAP } from '~/features/usage/constants'
import { formatValueWithUnit } from '~/features/usage/helpers'
import { DailyUsage, mergeDailyUsages } from '~/features/usage/utils'
import { useDetailedUsage, useUsage } from '~/hooks'
import { listBotsQueryOptions } from '~/queries'
import { useSuspenseQuery } from '~/services'

type Props = {
  selectedMonth: Date
  workspaceId: string
}

export const MonthlySummary = ({ selectedMonth, workspaceId }: Props) => {
  const bots = useTanstackSuspenseQuery(listBotsQueryOptions(workspaceId)).data

  const { ai_spend, invocation_calls, ...storageUsages } = useDetailedUsage({
    workspaceId,
    quotas: ['ai_spend', 'storage_count', 'table_row_count', 'knowledgebase_vector_storage', 'invocation_calls'],
    period: selectedMonth.toISOString().split('T')[0] ?? '',
  })

  const messagesUsages = { ai_spend, invocation_calls }

  const basicUsage = useUsage({
    workspaceId,
    quotas: ['workspace_member_count', 'bot_count', 'always_alive'],
    period: selectedMonth.toISOString().split('T')[0] ?? '',
  })

  const { data: dailyOpenAiUsage } = useSuspenseQuery(
    'workspaces_/$workspaceId_/usage/activities/$type/daily/$startDate/$endDate',
    {
      type: 'openai_spend',
      workspaceId,
      startDate: new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), 1).toISOString(),
      endDate: new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 0).toISOString(),
    }
  ) as { data: DailyUsage[] }

  const { data: dailyBingUsage } = useSuspenseQuery(
    'workspaces_/$workspaceId_/usage/activities/$type/daily/$startDate/$endDate',
    {
      type: 'bing_search_spend',
      workspaceId,
      startDate: new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), 1).toISOString(),
      endDate: new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 0).toISOString(),
    }
  ) as { data: DailyUsage[] }

  const getBotProperties = (botId: string) => bots.find((b) => b.id === botId) ?? { name: '[Deleted bot]' }

  const mapBotNameInUsage = (usage: DailyUsage) =>
    usage.metadata?.botId
      ? { ...usage, metadata: { ...usage.metadata, botName: getBotProperties(usage.metadata.botId).name } }
      : usage
  const dailyAiSpendUsage = mergeDailyUsages(
    dailyOpenAiUsage.map((usage) => mapBotNameInUsage(usage)),
    dailyBingUsage.map((usage) => mapBotNameInUsage(usage))
  )

  return (
    <div className="flex flex-col gap-4 @container">
      <CumulativeAiSpend data={dailyAiSpendUsage} selectedMonth={selectedMonth} />
      <div className="grid grid-cols-1 gap-4 @3xl:grid-cols-3">
        {Object.values(basicUsage).map(({ quota, value, type, percentage }) => {
          return (
            <ProgressBarCard
              key={type}
              title={QUOTA_TYPE_USER_FACING_PROPERTIES_MAP[type].name}
              description={QUOTA_TYPE_USER_FACING_PROPERTIES_MAP[type].description}
              value={percentage}
            >
              <div className="flex justify-between pb-1 text-sm text-neutral-900">
                <span>
                  {formatValueWithUnit(value, type)} of {formatValueWithUnit(quota, type)}
                </span>
                <span>{percentage.toFixed(0)}%</span>
              </div>
            </ProgressBarCard>
          )
        })}
      </div>
      <div className="grid grid-cols-1 grid-rows-[auto,1fr] gap-4 @4xl:grid-cols-2 @4xl:gap-y-0">
        {Object.entries(messagesUsages).map(([type, usage]) => (
          <BreakdownCard
            size="lg"
            key={type}
            usage={{
              ...usage,
              breakdown: usage.breakdown
                .filter((d) => d.value > 0)
                .map(({ botId, value }) => ({
                  id: botId,
                  value,
                  name: getBotProperties(botId).name,
                })),
            }}
            type={type as QuotaType}
          />
        ))}
      </div>
      <div className="grid grid-cols-1 grid-rows-[auto,1fr] gap-4 @4xl:grid-cols-3 @4xl:gap-y-0">
        {Object.entries(storageUsages).map(([type, usage]) => (
          <BreakdownCard
            key={type}
            size="sm"
            usage={{
              ...usage,
              breakdown: usage.breakdown
                .filter((d) => d.value > 0)
                .map(({ botId, value }) => ({
                  id: botId,
                  value,
                  name: getBotProperties(botId).name,
                })),
            }}
            type={type as QuotaType}
          />
        ))}
      </div>
    </div>
  )
}
